// settings/HamburgerMenu/index.tsx
import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import {
  currentVRMAtom,
  isSettingOpenAtom,
  languageAtom,
  standbyModeAtom,
  T2SEngineAtom,
  volumeAtom,
  vrmListAtom,
  vrmPathAtom,
} from "~/Atom";
import { PlaySound } from "~/SoundUtils";
import SidebarLayout from "./SidebarLayout";
import VoiceSection from "./sections/VoiceSection";
import CameraSection from "./sections/CameraSection";
import KnowledgeSection from "./sections/KnowledgeSection";
import DictionarySection from "./sections/DictionarySection";
import AISection from "./sections/AISection";
import { useVoiceSettings } from "./hooks/useVoiceSettings";
import "~/css/HamburgerMenu.css";
import ChatHistoryGraphSection from "./sections/ChatHistoryGraphSection";
import ConversationSection from "./sections/ConversationSection";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { VRM, VRMLoaderPlugin } from "@pixiv/three-vrm";
import { VRMAnimationLoaderPlugin } from "@pixiv/three-vrm-animation";
import { Character, VRMList } from "shared/Schemas";
import { currentLocale, t2SEngines } from "~/Commons";
import {
  createCharacter,
  getVRMFiles,
  getVRMGlobalName,
  getVRMName,
  getVRMURL,
  sortVRMsByCharacterOrder,
} from "~/vrm_utils/VRMUtils";
import { selectByName } from "~/components/db/CharacterDBUtils";
import * as db from "~/components/db/CharacterDBUtils";
import SystemSection from "~/components/settings/HamburgerMenu/sections/SystemSection";
import LayoutSection from "./sections/ScreenSection";
import FloorMapSection from "./sections/FloorMapSection";
import QRCodeSection from "~/components/settings/HamburgerMenu/sections/QRCodeSection";

/**
 * ハンバーガーメニューコンポーネント
 * 設定画面の表示と各セクションの管理を行う
 */
const HamburgerMenu: React.FC = () => {
  const [isSettingOpen, setIsSettingOpen] = useAtom(isSettingOpenAtom);
  const [standbyMode] = useAtom(standbyModeAtom);
  const [volume] = useAtom(volumeAtom);
  const [currentVRM] = useAtom(currentVRMAtom);
  const [loadedVrmList, setLoadedVrmList] = useState<VRM[]>([]);
  const [language] = useAtom(languageAtom);
  const [, setT2SEngine] = useAtom(T2SEngineAtom);
  const { updateSliderValue } = useVoiceSettings(currentVRM);
  const [vrmList] = useAtom(vrmListAtom);
  const [, setVRMPath] = useAtom(vrmPathAtom);

  const loader = new GLTFLoader();
  loader.register((parser) => {
    return new VRMLoaderPlugin(parser);
  });
  loader.register((parser) => {
    return new VRMAnimationLoaderPlugin(parser);
  });

  /**
   * スタンバイモード変更時の処理
   * スタンバイモードが1の場合、設定画面を閉じる
   */
  useEffect(() => {
    if (standbyMode === 1) {
      setIsSettingOpen(false);
    }
  }, [standbyMode, setIsSettingOpen]);

  /**
   * 設定画面の開閉に追従して音量関連UIを再計算
   */
  useEffect(() => {
    if (isSettingOpen) {
      updateSliderValue();
    }
  }, [isSettingOpen, updateSliderValue]);

  // ttsEngine設定
  const setTTSEngine = async (vrm: VRM | null) => {
    const characters = (await db.selectAll()) as Character[];
    const character = characters.find((character) => character.name === getVRMName(vrm));

    const ttsEngine = character?.tts_engine;
    if (language === currentLocale && ttsEngine) {
      setT2SEngine(ttsEngine);
    } else {
      setT2SEngine(t2SEngines.openai);
    }
  };

  // VRMの情報を取得し、VRMをロードする関数
  const loadVRM = async ({ url, name }: VRMList): Promise<VRM | null> => {
    return new Promise((resolve) => {
      if (!url) {
        console.error("モデルのパスがありません");
        resolve(null);
        return;
      }
      loader.load(
        url,
        async (tmpGltf) => {
          try {
            const vrm = tmpGltf.userData.vrm;
            if (vrm === null) {
              resolve(null);
              return;
            }

            const globalName = name.replace(/\.vrm$/i, "");
            const searchName = getVRMName(vrm);
            const character = await selectByName(searchName);
            if (character === null) {
              console.error("CharacterNotFound:キャラクターを新規作成します");
              await createCharacter(searchName, globalName, "VOICEVOX");
            }
            resolve(vrm);
          } catch (error) {
            console.error("VRM処理エラー:", error);
            resolve(null);
          }
        },
        undefined,
        (error) => {
          console.error("An error happened");
          console.error(error);
          resolve(null);
        }
      );
    });
  };

  // キャラクターアイコンクリック時の処理
  const handleCharacterSelect = async (vrm: VRM) => {
    const globalName = await getVRMGlobalName(vrm);
    const vrmData = vrmList.filter((vrm) => vrm.name === globalName + ".vrm")[0];
    setVRMPath(vrmData.url);
    setTTSEngine(vrm);
    PlaySound("push", volume);
  };

  // VRMの情報を取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        // VRMの情報を取得
        const vrmData = (await getVRMFiles()) as VRMList[];
        const characters = await db.selectAvailableCharacters(vrmData);
        for (const vrm of vrmData) {
          vrm.url = getVRMURL(vrmData, vrm.name);
        }

        // VRMをロード
        const results = await Promise.all(vrmData.map((vrm) => loadVRM(vrm)));

        // charactersの登録順にVRMをソート
        const filteredResults = sortVRMsByCharacterOrder(results, characters);

        setLoadedVrmList(filteredResults);
      } catch (error) {
        console.error("Error fetching idle motion buffer:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      <div className="hamburger-menu">
        <SidebarLayout>
          <AISection
            loadedVrm={currentVRM}
            loadedVrmList={loadedVrmList}
            handleCharacterSelect={handleCharacterSelect}
          />
          <VoiceSection loadedVrm={currentVRM} isExpression={true} />
          <ConversationSection />
          <CameraSection />
          <KnowledgeSection />
          <DictionarySection />
          <ChatHistoryGraphSection />
          <SystemSection />
          <LayoutSection />
          <FloorMapSection />
          <QRCodeSection />
        </SidebarLayout>
      </div>
    </>
  );
};

export default HamburgerMenu;
